/**
 * TeacherOpenShifts (BON-242) — "Eligible shifts only: region-matched,
 * qualified, and free on your calendar. Requesting isn't a booking; the RD
 * confirms one person per shift."
 *
 * The position chips are a user FILTER, not an eligibility gate — the server
 * already removed anything you aren't qualified for.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { requestShift, withdrawShiftRequest } from '../../../api/shiftRequests';
import { CREW_POSITION_LABELS } from '../../../lib/crewPositions';
import { SegmentedControl } from '../primitives';
import { BucketError, BucketLoading } from '../regional/BucketStates';

import { OpenShiftCard } from './OpenShiftCard';
import { Empty, EmptyState } from './OpenShiftsEmptyStates';
import { useTeacherShifts, type EligibleShiftEntry } from './teacherApi';
import { MY_REQUESTS_KEY } from './TeacherMyRequests';

/** Mirrors CrewPosition exactly. An earlier cut folded trainees into
 *  "Assistant" via `!== 'teacher'`, which produced cards badged "Trainee"
 *  under the Assistant chip — the filter contradicting the card. */
type PositionFilter = 'all' | 'teacher' | 'assistant' | 'teacher_in_training';
const FILTERS: ReadonlyArray<{ value: PositionFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'teacher', label: CREW_POSITION_LABELS.teacher },
  { value: 'assistant', label: CREW_POSITION_LABELS.assistant },
  { value: 'teacher_in_training', label: CREW_POSITION_LABELS.teacher_in_training },
];

function matchesFilter(shift: EligibleShiftEntry, filter: PositionFilter): boolean {
  return filter === 'all' || shift.position === filter;
}

/** Request/withdraw plus the cache invalidation both share. */
function usePickupActions() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['dashboards.teacher', 'shifts'] });
    // "My requests" renders the same rows under a different key — without this
    // a teacher who requests here and switches tabs inside the 30s staleTime
    // sees their brand-new request missing.
    qc.invalidateQueries({ queryKey: MY_REQUESTS_KEY });
  };
  const onError = (err: Error) => {
    setError(err.message);
    // A failure usually means the board is stale — someone withdrew the shift,
    // or an RD cancelled the workshop. Refetch so the dead row disappears.
    refresh();
  };
  const opts = { onMutate: () => setError(null), onSuccess: refresh, onError };

  const request = useMutation({ mutationFn: requestShift, ...opts });
  const withdraw = useMutation({ mutationFn: withdrawShiftRequest, ...opts });

  return {
    error,
    busy: request.isPending || withdraw.isPending,
    onRequest: (id: string) => request.mutate(id),
    onWithdraw: (id: string) => withdraw.mutate(id),
  };
}

export default function TeacherOpenShifts() {
  const q = useTeacherShifts();
  const [filter, setFilter] = useState<PositionFilter>('all');
  const { error, busy, onRequest, onWithdraw } = usePickupActions();

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;

  const { shifts, filtered, gap } = q.data.data;
  const visible = shifts.filter((s) => matchesFilter(s, filter));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
          {shifts.length} shift{shifts.length === 1 ? '' : 's'} you can pick up
        </span>
        <SegmentedControl
          value={filter}
          onChange={setFilter}
          options={FILTERS}
          ariaLabel="Filter shifts by position"
        />
      </div>

      {error && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
          {error}
        </div>
      )}

      {shifts.length === 0 ? (
        <EmptyState filtered={filtered} gap={gap} />
      ) : visible.length === 0 ? (
        // Eligible shifts exist, the chip just hides them all — without this
        // the page is blank under a non-zero header count, which reads as a
        // rendering failure.
        <Empty
          title="Nothing under this filter"
          body="You have open shifts, but none for this position. Switch back to All to see them."
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          {visible.map((shift) => (
            <OpenShiftCard
              key={shift.id}
              shift={shift}
              busy={busy}
              onRequest={onRequest}
              onWithdraw={onWithdraw}
            />
          ))}
        </div>
      )}
    </div>
  );
}
